import { Component, OnInit, OnDestroy, Optional, Inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { TradingService } from '../../services/common/trading-service.service';
import { PershingTranslationService } from '@pershing/pershing-core';
import { TradingExchangeOrder, OrderType, TransactionType, OrderDuration, MFDvreType, ContributionType } from '../../models/common';
import { isEstimatedValueApplicable } from '../../utils';
import { CurrencyPipe, DecimalPipe, LowerCasePipe, DatePipe, registerLocaleData } from '@angular/common';
import moment_tz_ from 'moment-timezone';
import { DistributionType, MutualfundTradingOrder, PayToType } from '../../models';
const moment = moment_tz_;
import es from '@angular/common/locales/es';
@Component({
  selector: 'trading-order-summary',
  templateUrl: './order-summary.component.html',
  styleUrls: ['./order-summary.component.css'],
  providers: [CurrencyPipe, DecimalPipe, LowerCasePipe, DatePipe]
})

export class OrderSummaryComponent implements OnInit, OnDestroy {
  onRawOrderSubject: Subscription;
  holdingsSub: Subscription;
  orderSummaryText: string;
  orderTypeMessage: string;
  trailingTxt: string;
  text: string;
  formattedQty: any;
  toggleOn: boolean = false;
  isBuyOrder = false;
  notAvailableLabel: string;
  transactionLabel: string;
  language: string = '';
  hasPreviouslyPurchased = false;
  estPrincipalSub: Subscription;

  constructor(private tradingService: TradingService, private translationService: PershingTranslationService, private currencyPipe: CurrencyPipe,
    private lowerCasePipe: LowerCasePipe, private decimalPipe: DecimalPipe, private datePipe: DatePipe, @Optional() @Inject('AppSettings') appSettings: any) {
    if (appSettings?.language) {
      this.language = appSettings.language;
      if (this.language === 'es') {
        registerLocaleData(es);
      }
    }
  }

  ngOnInit() {
    this.onRawOrderSubject = this.tradingService.onRawOrderObservable$.subscribe((order: any) => {
      this.onOrder(order);
    }
    );
  }

  private onOrder(order: any): void {
    if (!!this.tradingService.tradingEntitlements && this.tradingService.tradingEntitlements.isRetailUser) {
      if (!!order && this.tradingService.currentContainer) {
        this.isBuyOrder = order.transactionType === TransactionType.BUY || order.transactionType === TransactionType.BUY_TO_COVER;
        if (this.tradingService.currentContainer === 'Equity') {
          this.generateTextEquity(order);
        } else if (this.tradingService.currentContainer === 'Extended') {
          this.generateTextExtendedHrs(order);
        }
        else if (this.tradingService.currentContainer === 'Option') {
          this.estPrincipalSub = this.tradingService.estPrincipleAmountSubject$.subscribe((amt) => order.commission.principalAmt = amt);
          this.generateTextOptions(order);
        } else if (this.tradingService.currentContainer === 'MutualFund') {
          this.generateTextMF(order);
        } else if (this.tradingService.currentContainer === 'Plan529') {
          this.generateText529(order);
        }
      }
    }
  }

  generateTextEquity(order) {
    const equityOrder = order as TradingExchangeOrder;
    if (equityOrder.transactionType === TransactionType.SELL_ALL) {
      this.toggleOn = true;
    }

    let textString = '';
    let orderTypeMessage = this.generateOrderTypeMessage(order);
    this.formatOrderData(equityOrder);
    order.ordQty = this.decimalPipe.transform(order.ordQty, '1.0', this.language === 'es' ? 'es' : '');

    if (!equityOrder.transactionType || !equityOrder.quote || !equityOrder.quote.symId) {
      this.toggleOn = false;
      return '';
    }

    if (!!equityOrder.orderType && equityOrder.orderType.substring(0, 5) === 'TRAIL') {
      return this.generateTextTrailingStop(order, orderTypeMessage);
    } else {
      textString = this.getTextForEquity(equityOrder, orderTypeMessage);
    }

    if (textString) {
      this.translationService.get(textString, order).subscribe((text) => {
        textString = text;
        if (!!orderTypeMessage) {
          textString = textString.replace('{{orderTypeMessage}}', orderTypeMessage);
        }
      });
      this.orderSummaryText = textString;
    }

    return textString;
  }

  getTextForEquity(equityOrder: TradingExchangeOrder, orderTypeMessage: string): string {
    let textString = '';
    if (equityOrder.transactionType && equityOrder.quote && equityOrder.quote.symId && equityOrder.quote.description && equityOrder.ordQty && !!equityOrder.orderType
      && orderTypeMessage && equityOrder.duration && equityOrder.accountType && equityOrder.solicited) {
      textString = 'labels.orderSummary.sevenPartMsg';
    } else if (equityOrder.transactionType && equityOrder.quote && equityOrder.quote.symId && equityOrder.quote.description && equityOrder.ordQty && !!equityOrder.orderType
      && orderTypeMessage && equityOrder.duration && equityOrder.accountType) {
      textString = 'labels.orderSummary.sixPartMsg';
    } else if (equityOrder.transactionType && equityOrder.quote && equityOrder.quote.symId && equityOrder.quote.description && equityOrder.ordQty && !!equityOrder.orderType
      && orderTypeMessage && equityOrder.duration) {
      textString = 'labels.orderSummary.fivePartMsg';
    } else if (equityOrder.transactionType && equityOrder.quote && equityOrder.quote.symId && equityOrder.quote.description && equityOrder.ordQty && !!equityOrder.orderType
      && orderTypeMessage) {
      textString = 'labels.orderSummary.fourPartMsg';
    } else if (equityOrder.transactionType && equityOrder.quote && equityOrder.quote.symId && equityOrder.quote.description && equityOrder.ordQty) {
      textString = 'labels.orderSummary.threePartMsg';
    } else if (equityOrder.transactionType && equityOrder.quote && equityOrder.quote.symId && equityOrder.quote.description) {
      textString = 'labels.orderSummary.twoPartMsg';
      this.toggleOn = true;
    }

    return textString;
  }

  generateTextOptions(order) {
    const optionsOrder = order as TradingExchangeOrder;
    let textString = '';
    let options = '';
    let quoteDataReady = false;

    let orderTypeMessage = this.generateOrderTypeMessage(order);
    this.formatOrderData(optionsOrder);
    order.ordQty = this.decimalPipe.transform(order.ordQty, '1.0', this.language === 'es' ? 'es' : '');

    if (optionsOrder.quote && order.symId && optionsOrder.quote.description) {
      options = 'labels.orderSummary.strikePriceExpirationDate';
      quoteDataReady = true;
      this.toggleOn = true;
    }
    if (!optionsOrder.transactionType || !optionsOrder.quote || !optionsOrder.quote.symId) {
      this.toggleOn = false;
      return '';
    } else {
      textString = this.getTextForOption(optionsOrder, orderTypeMessage, options, quoteDataReady);
    }

    if (textString) {
      this.translationService.get(textString, order).subscribe((text) => {
        this.translationService.get(options, order).subscribe((expirationDate) => {
          options = expirationDate;
          textString = text;
          textString = textString.replace('{{orderTypeMessage}}', orderTypeMessage);
          textString = textString.replace('{{options}}', options);
        });
      });
      this.orderSummaryText = textString;
    }

    return textString;
  }

  getTextForOption(optionsOrder: TradingExchangeOrder, orderTypeMessage: string, options: string, quoteDataReady: boolean): string {
    let textString = '';
    if (optionsOrder.transactionType && quoteDataReady && optionsOrder.ordQty && options
      && optionsOrder.duration && optionsOrder.accountType && orderTypeMessage && optionsOrder.solicited) {
      textString = 'labels.orderSummary.sevenPartMsg_OP';
    } else if (optionsOrder.transactionType && quoteDataReady && optionsOrder.ordQty && options
      && optionsOrder.duration && optionsOrder.accountType && orderTypeMessage) {
      textString = 'labels.orderSummary.sixPartMsg_OP';
    } else if (optionsOrder.transactionType && quoteDataReady && optionsOrder.ordQty && options
      && optionsOrder.duration && orderTypeMessage) {
      textString = 'labels.orderSummary.fivePartMsg_OP';
    } else if (optionsOrder.transactionType && quoteDataReady && optionsOrder.ordQty && options
      && orderTypeMessage) {
      textString = 'labels.orderSummary.fourPartMsg_OP';
    } else if (optionsOrder.transactionType && quoteDataReady && optionsOrder.ordQty) {
      textString = 'labels.orderSummary.threePartMsg_OP';
    } else if (optionsOrder.transactionType && quoteDataReady && optionsOrder.quote.description) {
      textString = 'labels.orderSummary.twoPartMsg_OP';
      this.toggleOn = true;
    }
    return textString;
  }

  generateTextExtendedHrs(order) {
    const equityExtendedOrder = order as TradingExchangeOrder;
    let textString = '';
    let durationText = '';
    let options = '';
    let orderTypeMessage = '';
    order.ordQty = this.decimalPipe.transform(order.ordQty, '1.0', this.language === 'es' ? 'es' : '');

    if (!!equityExtendedOrder.limitPrice) {
      orderTypeMessage = 'labels.orderSummary.orderTypeExt';
      order.limitPrice = this.convertToDecimal(order.limitPrice);
      this.translationService.get(orderTypeMessage, equityExtendedOrder).subscribe((limitMsg) => {
        orderTypeMessage = limitMsg;
      });
    }

    if (equityExtendedOrder.transactionType === TransactionType.SELL_ALL) {
      this.toggleOn = true;
    }
    order.transactionType = this.formatTransactionType(order);


    if (!equityExtendedOrder.transactionType || !equityExtendedOrder.quote || !equityExtendedOrder.quote.symId) {
      this.toggleOn = false;
      return '';
    }

    if (equityExtendedOrder.duration === OrderDuration.PREMKT) {
      durationText = 'labels.orderSummary.preDurationText';
      options = 'labels.orderSummary.preTimeDuration';
    } else if (equityExtendedOrder.duration === OrderDuration.AFTMKT) {
      durationText = 'labels.orderSummary.afterDurationText';
      options = 'labels.orderSummary.afterTimeDuration';
    }
    this.formatOrderData(equityExtendedOrder);
    textString = this.getTextForExtendedHrs(equityExtendedOrder, orderTypeMessage);
    textString = this.getFinalTextForExtendedHrs(textString, orderTypeMessage, durationText, options, order);
    return textString;

  }

  getTextForExtendedHrs(equityExtendedOrder: TradingExchangeOrder, orderTypeMessage: string): string {
    let textString = '';
    if (equityExtendedOrder.transactionType && equityExtendedOrder.quote && equityExtendedOrder.quote.symId &&
      equityExtendedOrder.quote.description && equityExtendedOrder.ordQty && orderTypeMessage && equityExtendedOrder.accountType && equityExtendedOrder.solicited) {
      textString = 'labels.orderSummary.sevenPartMsgExt';
    } else if (equityExtendedOrder.transactionType && equityExtendedOrder.quote && equityExtendedOrder.quote.symId
      && equityExtendedOrder.quote.description && equityExtendedOrder.ordQty && orderTypeMessage && equityExtendedOrder.accountType) {
      textString = 'labels.orderSummary.fivePartMsgExt';
    } else if (equityExtendedOrder.transactionType && equityExtendedOrder.quote && equityExtendedOrder.quote.symId && equityExtendedOrder.quote.description
      && equityExtendedOrder.ordQty && orderTypeMessage) {
      textString = 'labels.orderSummary.fourPartMsgExt';
    } else if (equityExtendedOrder.transactionType && equityExtendedOrder.quote && equityExtendedOrder.quote.symId && equityExtendedOrder.quote.description
      && equityExtendedOrder.ordQty) {
      textString = 'labels.orderSummary.threePartMsgExt';
    } else if (equityExtendedOrder.transactionType && equityExtendedOrder.quote && equityExtendedOrder.quote.symId && equityExtendedOrder.quote.description) {
      textString = 'labels.orderSummary.twoPartMsgExt';
      this.toggleOn = true;
    }
    return textString;
  }

  getFinalTextForExtendedHrs(textString: string, orderTypeMessage: string, durationText: string, options: string, order: any): string {
    if (textString && durationText && options) {
      this.translationService.get(textString, order).subscribe((text) => {
        textString = text;
        this.translationService.get(durationText, order).subscribe((durationTxt) => {
          durationText = durationTxt;
          textString = textString.replace('{{durationText}}', durationText);
          this.translationService.get(options, order).subscribe((optionTxt) => {
            options = optionTxt;
            textString = textString.replace('{{options}}', options);
            if (!!orderTypeMessage) {
              textString = textString.replace('{{orderTypeMessage}}', orderTypeMessage);
            }
          });
        });
      });
      this.orderSummaryText = textString;
    }
    return textString;
  }

  generateTextMF(order) {
    const mfOrder = order as MutualfundTradingOrder;
    let textString = '';

    let optionsExc;
    let isExchangeSymIdAvailable = !!order.exchgToSymId;
    this.formatOrderData(mfOrder);
    if (!mfOrder.transactionType || !mfOrder.quote.symId || !mfOrder.quote) {
      this.toggleOn = false;
      return '';
    }

    let dvre = this.getDvreForTextMF(mfOrder);
    let label = this.getLabelForTextMF(order);
    let amount = this.getAmountForTextMF(mfOrder, label);
    this.hasPreviouslyPurchased = this.checkIfPreviouslyPurchased(mfOrder);

    if (this.isExchangeOrExchangeAll(mfOrder.transactionType)) {
      if (!isExchangeSymIdAvailable) {
        return textString;
      } else {
        label = 'labels.orderSummary.exchangeText';
        this.translationService.get(label, order).subscribe((amt) => {
          optionsExc = amt;
          optionsExc = optionsExc.replace('exchangeSymbol', order.exchgToSymId);
        });
      }
    }

    dvre = this.getFinalDvreForTextMF(dvre, mfOrder, isExchangeSymIdAvailable);
    if (mfOrder.transactionType && mfOrder.quote?.symId) {
      textString = this.getTextForMF(mfOrder, dvre, amount, isExchangeSymIdAvailable);
    }
    if (textString) {
      textString = this.getFinalTextForMF(textString, mfOrder, order, amount, optionsExc, dvre);
    }

    return textString;
  }

  getDvreForTextMF(mfOrder: MutualfundTradingOrder): string {
    let dvre = '';
    if (!(this.isSellAndBuyOrSellAllAndBuy(mfOrder.transactionType) || mfOrder.transactionType === TransactionType.BUY) ||
    (mfOrder.transactionType === TransactionType.BUY && this.hasPreviouslyPurchased)) {
      dvre = '.';
    }else {
      dvre = this.getDvreFromMFDvreTypeForTextMF(mfOrder,dvre);
    } 

    return dvre;
  }

  getDvreFromMFDvreTypeForTextMF(mfOrder: MutualfundTradingOrder, dvre: string): string {
    switch (mfOrder.dvre) {
      case MFDvreType.DIVIDEND:
        dvre = mfOrder.transactionType === TransactionType.BUY ? 'labels.orderSummary.dividendOnly' : 'labels.orderSummary.swapDividendOnly';
        break;
      case MFDvreType.CASH:
        dvre = mfOrder.transactionType === TransactionType.BUY ? 'labels.orderSummary.cash' : 'labels.orderSummary.swapCash';
        break;
      case MFDvreType.REINVEST:
        dvre = mfOrder.transactionType === TransactionType.BUY ? 'labels.orderSummary.reinvest' : 'labels.orderSummary.swapCash';
        break;
      case MFDvreType.CAPITAL:
        dvre = mfOrder.transactionType === TransactionType.BUY ? 'labels.orderSummary.capital' : 'labels.orderSummary.swapCapital';
        break;
    }

    return dvre;
  }

  checkIfPreviouslyPurchased(mfOrder: MutualfundTradingOrder): boolean {
    let holdings = [];
    this.holdingsSub = this.tradingService.holdingSubject$.subscribe((resp) => {
      holdings = resp;
    });

    return this.checkHoldingsForPurchase(holdings, mfOrder);

  }

  checkHoldingsForPurchase(holdings: any, mfOrder: MutualfundTradingOrder): boolean {
    let hasPreviouslyPurchased = false;
    if (!!holdings && holdings.length > 0) {
      for (let holding of holdings) {
        if (holding.symbol === mfOrder.quote.symId) {
          hasPreviouslyPurchased = true;
          break;
        }
      }
    }

    return hasPreviouslyPurchased;
  }

  getFinalDvreForTextMF(dvre: string, mfOrder: MutualfundTradingOrder, isExchangeSymIdAvailable: boolean): string {
    if (this.isExchangeOrExchangeAll(mfOrder.transactionType)) {
      dvre = isExchangeSymIdAvailable ? 'labels.orderSummary.reinvest_exchange' : 'labels.orderSummary.reinvest_exchangeSub';
    }

    if (mfOrder.transactionType === TransactionType.BUY && this.hasPreviouslyPurchased) {
      dvre = 'labels.orderSummary.subsequent_buy';
    }

    if (this.hasPreviouslyPurchased && this.isSellAndBuyOrSellAllAndBuy(mfOrder.transactionType)) {
      dvre = 'labels.orderSummary.swapSubsequent_buy';
    }
    return dvre;
  }

  getLabelForTextMF(order): any {
    const mfOrder = order as MutualfundTradingOrder;
    let label = '';
    if (!!mfOrder.shareQty) {
      label = 'labels.orderSummary.mfAmountNoNavShare';
      this.translationService.get(label, order).subscribe((labelText) => {
        label = labelText;
        label = label.replace('{{tempShareQty}}', order.shareQty.toString());
      });

    }
    return label;
  }

  getAmountForTextMF(mfOrder: MutualfundTradingOrder, label: string): any {
    let amount: any;
    if (!!mfOrder.dollarQty) {
      amount = mfOrder.dollarQty;
    } else if (!!mfOrder.shareQty) {
      amount = label;
    }
    return amount;
  }

  getTextForMF(mfOrder: MutualfundTradingOrder, dvre: string, amount: any, isExchangeSymIdAvailable: boolean): string {
    let textString = '';
    let appendText = this.isAppendText(mfOrder);
    if (amount && dvre && appendText) {
      this.toggleOn = true;
      textString = this.getTextFromAmountDvreAppendTextForMF(textString, mfOrder, isExchangeSymIdAvailable);
    } else if (amount && mfOrder.dvre && appendText) {
      textString = 'labels.orderSummary.fourPartMsg_MF';
    } else if (amount && mfOrder.dvre && !appendText) {
      this.toggleOn = true;
      textString = this.isExchangeOrExchangeAll(mfOrder.transactionType) ? 'labels.orderSummary.fourPartMsgE_MF2' : 'labels.orderSummary.fourPartMsg_MF2';
    } else if (!!amount) {
      this.toggleOn = true;
      textString = this.isSellAndBuyOrSellAllAndBuy(mfOrder.transactionType) ? 'labels.orderSummary.threePartMsg_MFSwap' : 'labels.orderSummary.threePartMsg_MF';
    } else {
      this.toggleOn = true;
      textString = this.getTextFromSymIdForMF(textString, mfOrder, isExchangeSymIdAvailable);

    }
    return textString;
  }

  getTextFromSymIdForMF(textString: string, mfOrder: MutualfundTradingOrder, isExchangeSymIdAvailable: boolean): string {

    if (this.isSellAndBuyOrSellAllAndBuy(mfOrder.transactionType)) {
      textString = !isExchangeSymIdAvailable ? 'labels.orderSummary.swapMsg_MF' : 'labels.orderSummary.twoPartMsg_MFSwap';
    } else if (this.isExchangeOrExchangeAll(mfOrder.transactionType)) {
      textString = 'labels.orderSummary.twoPartMsgE_MF';
    } else {
      textString = 'labels.orderSummary.twoPartMsg_MF';
    }
    return textString;
  }

  getTextFromAmountDvreAppendTextForMF(textString: string, mfOrder: MutualfundTradingOrder, isExchangeSymIdAvailable: boolean): string {

    if (this.isSellAndBuyOrSellAllAndBuy(mfOrder.transactionType)) {
      if (!isExchangeSymIdAvailable) {
        textString = 'labels.orderSummary.threePartMsg_MFSwap';
      } else if (this.hasPreviouslyPurchased) {
        textString = 'labels.orderSummary.fourPartMsg_MFSwap2';
      } else {
        textString = 'labels.orderSummary.fourPartMsg_MFSwap';
      }
    } else if (this.isExchangeOrExchangeAll(mfOrder.transactionType)) {
      textString = 'labels.orderSummary.fivePartMsgE_MF';
    } else {
      textString = 'labels.orderSummary.fivePartMsg_MF';
    }
    return textString;
  }

  isAppendText(mfOrder: MutualfundTradingOrder): boolean {
    if (!(this.isSellAndBuyOrSellAllAndBuy(mfOrder.transactionType) || mfOrder.transactionType === TransactionType.BUY)
    || (mfOrder.transactionType === TransactionType.BUY && this.hasPreviouslyPurchased)) {
      return false;
    } else {
      return true;
    }
  }

  getFinalTextForMF(textString: string, mfOrder: MutualfundTradingOrder, order: any, amount: any, optionsExc: any, dvre: string): string {
    let prospectusLabel = mfOrder.readProspectus ? 'labels.orderSummary.prospectusText' : ' ';
    let options = mfOrder.solicited ? 'labels.orderSummary.advisorTextSummary' : ' ';
    this.translationService.get(textString, order).subscribe((finalMsg) => {
      textString = finalMsg;
      if (!!mfOrder.transactionType) {
        order.transactionType = this.formatTransactionType(mfOrder);
        textString = textString.replace('{{transaction}}', mfOrder.transactionType);
      }
      if (!!amount) {
        textString = textString.replace('{{amount}}', amount);
      }
      if (!!optionsExc) {
        this.translationService.get(optionsExc, order).subscribe((optionsExecMsg) => {
          optionsExc = optionsExecMsg;
          textString = textString.replace('{{optionsExc}}', optionsExc);
        });
      }
      if (!!dvre) {
        this.translationService.get(dvre, order).subscribe((dvreMsg) => {
          dvre = dvreMsg;
          textString = textString.replace('{{dvreType}}', dvre);
        });
      }
      if (!!prospectusLabel) {
        this.translationService.get(prospectusLabel, order).subscribe((prospMsg) => {
          prospectusLabel = prospMsg;
          textString = textString.replace('{{prospectusLabel}}', prospectusLabel);
        });
      }
      if (!!options) {
        this.translationService.get(options, order).subscribe((optionsMsg) => {
          options = optionsMsg;
          textString = textString.replace('{{options}}', options);
        });
      }
    });
    this.orderSummaryText = textString;
    return textString;
  }

  getDvreForText529(fiveTwoNineOrder: MutualfundTradingOrder) {
    let dvre = '';
    if (fiveTwoNineOrder.dvre === MFDvreType.DIVIDEND) {
      dvre = 'labels.orderSummary.dividendOnly';

    } else if (fiveTwoNineOrder.dvre === MFDvreType.CASH) {
      dvre = 'labels.orderSummary.cash';

    } else if (fiveTwoNineOrder.dvre === MFDvreType.REINVEST) {
      dvre = 'labels.orderSummary.reinvest';

    } else if (fiveTwoNineOrder.dvre === MFDvreType.CAPITAL) {
      dvre = 'labels.orderSummary.capital';
    }
    return dvre;
  }

  generateText529(order) {
    const fiveTwoNineOrder = order as MutualfundTradingOrder;
    let textString = '';
    let optionsExc;
    let isExchangeSymIdAvailable = !!order.exchgToSymId;
    this.formatOrderData(fiveTwoNineOrder);
    if (!fiveTwoNineOrder.transactionType || !fiveTwoNineOrder.quote.symId || !fiveTwoNineOrder.quote) {
      this.toggleOn = false;
      return '';
    }

    let dvre = this.getDvreForText529(fiveTwoNineOrder);
    let label = '';
    if (this.isExchangeOrExchangeAll(fiveTwoNineOrder.transactionType)) {
      if (!isExchangeSymIdAvailable) {
        return textString;
      } else {
        label = 'labels.orderSummary.exchangeText';
        this.translationService.get(label, order).subscribe((amt) => {
          optionsExc = amt;
          optionsExc = optionsExc.replace('exchangeSymbol', order.exchgToSymId);
        });
      }
      dvre = 'labels.orderSummary.reinvest_exchange';
    } else {
      label = this.getLabelForText529(fiveTwoNineOrder);
    }
    let amount = this.getAmountForText529(fiveTwoNineOrder);

    if (fiveTwoNineOrder.transactionType && fiveTwoNineOrder.quote && fiveTwoNineOrder.quote.symId) {
      if (this.isExchangeOrExchangeAll(fiveTwoNineOrder.transactionType)) {
        if (isExchangeSymIdAvailable) {
          label = 'labels.orderSummary.twoPartMsgE_529';
          this.translationService.get(label, order).subscribe((amt) => {
            optionsExc = amt;
          });
        }
      }
    }

    textString = this.getTextFor529(order, amount, dvre, label, isExchangeSymIdAvailable, optionsExc);
    if (textString) {
      this.translationService.get(textString, fiveTwoNineOrder).subscribe((finalMsg) => {
        textString = finalMsg;
        textString = this.getFinalTextFor529(textString, order, amount, optionsExc, dvre, label);
      });
      this.orderSummaryText = textString;
    }
    return textString;
  }

  isExchangeOrExchangeAll(transactionType: TransactionType): boolean {
    return (transactionType === TransactionType.EXCHANGE || transactionType === TransactionType.EXCHANGE_ALL) ? true : false;
  }

  isSellAndBuyOrSellAllAndBuy(transactionType: TransactionType): boolean {
    return (transactionType === TransactionType.SELL_AND_BUY || transactionType === TransactionType.SELL_ALL_AND_BUY) ? true : false;
  }

  isRedeemOrRedeemAll(transactionType: TransactionType): boolean {
    return (transactionType === TransactionType.REDEEM || transactionType === TransactionType.REDEEM_ALL) ? true : false;
  }

  getAmountForText529(order: any): any {
    const fiveTwoNineOrder = order as MutualfundTradingOrder;
    let amount: any;
    let qtyLabel;
    if (!!fiveTwoNineOrder.dollarQty) {
      amount = fiveTwoNineOrder.dollarQty;
    } else if (!!fiveTwoNineOrder.shareQty) {
      qtyLabel = 'labels.orderSummary.mfAmountNoNavShare';
      this.translationService.get(qtyLabel, order).subscribe((labelText) => {
        qtyLabel = labelText;
        qtyLabel = qtyLabel.replace('{{tempShareQty}}', order.shareQty);
      });
      amount = qtyLabel;
    }
    return amount;
  }

  getLabelForText529(order: any) {
    const fiveTwoNineOrder = order as MutualfundTradingOrder;
    let label = '';
    if (fiveTwoNineOrder.transactionType === TransactionType.BUY) {
      if (order.contributionType === ContributionType.CURRENT_YEAR) {
        label = 'labels.orderSummary.contributionTypeCY';
      } else if (order.contributionType === ContributionType.PRIOR_YEAR) {
        label = 'labels.orderSummary.contributionTypePY';
      }

    } else if (this.isRedeemOrRedeemAll(fiveTwoNineOrder.transactionType)) {
      label = this.getLabelFromDistributionTypeFor529(fiveTwoNineOrder);
    }
    return label;
  }

  getLabelFromDistributionTypeFor529(fiveTwoNineOrder: MutualfundTradingOrder) {
    let label = '';
    if (fiveTwoNineOrder.distributionType === DistributionType.QUALIFIED && fiveTwoNineOrder.payToType === PayToType.BENEFICIARY) {
      label = 'labels.orderSummary.distributionTypeQ_payToB';
    } else if (fiveTwoNineOrder.distributionType === DistributionType.QUALIFIED && fiveTwoNineOrder.payToType === PayToType.ACCOUNT_HOLDER) {
      label = 'labels.orderSummary.distributionTypeQ_payToA';
    } else if (fiveTwoNineOrder.distributionType === DistributionType.QUALIFIED) {
      label = 'labels.orderSummary.distributionTypeQ';
    } else if (fiveTwoNineOrder.distributionType === DistributionType.NON_QUALIFIED && fiveTwoNineOrder.payToType === PayToType.BENEFICIARY) {
      label = 'labels.orderSummary.distributionTypeNQ_payToB';
    } else if (fiveTwoNineOrder.distributionType === DistributionType.NON_QUALIFIED && fiveTwoNineOrder.payToType === PayToType.ACCOUNT_HOLDER) {
      label = 'labels.orderSummary.distributionTypeNQ_payToA';
    } else if (fiveTwoNineOrder.distributionType === DistributionType.NON_QUALIFIED) {
      label = 'labels.orderSummary.distributionTypeNQ';
    }
    return label;
  }

  getTextFor529(order: any, amount: any, dvre: string, label: string, isExchangeSymIdAvailable: boolean, optionsExc: any): any {
    const fiveTwoNineOrder = order as MutualfundTradingOrder;
    let textString = '';
    let appendText = true;
    if (this.isRedeemOrRedeemAll(fiveTwoNineOrder.transactionType)) {
      appendText = false;
    }
    if (fiveTwoNineOrder.transactionType && fiveTwoNineOrder.quote && fiveTwoNineOrder.quote.symId && amount
      && dvre && appendText && label) {
      this.toggleOn = true;
      if (this.isExchangeOrExchangeAll(fiveTwoNineOrder.transactionType)) {
        textString = 'labels.orderSummary.fivePartMsgE_529';
      } else {
        textString = 'labels.orderSummary.fivePartMsg_529';
      }
    } else if (fiveTwoNineOrder.transactionType && fiveTwoNineOrder.quote && fiveTwoNineOrder.quote.symId && amount && fiveTwoNineOrder.dvre && !appendText) {
      textString = 'labels.orderSummary.fivePartMsg2_529';
    } else if (fiveTwoNineOrder.transactionType && fiveTwoNineOrder.quote && fiveTwoNineOrder.quote.symId && amount && label) {
      textString = 'labels.orderSummary.threePartMsg_529';
      this.toggleOn = true;
    } else if (fiveTwoNineOrder.transactionType && fiveTwoNineOrder.quote && fiveTwoNineOrder.quote.symId) {
      textString = this.getTextFromSymbolFor529(textString, order, isExchangeSymIdAvailable, label, optionsExc);


    }
    return textString;
  }

  getTextFromSymbolFor529(textString: string, order: any, isExchangeSymIdAvailable: boolean, label: string, optionsExc: any): string {
    const fiveTwoNineOrder = order as MutualfundTradingOrder;
    this.toggleOn = true;
    if (this.isExchangeOrExchangeAll(fiveTwoNineOrder.transactionType)) {
      if (isExchangeSymIdAvailable) {
        label = 'labels.orderSummary.twoPartMsgE_529';
        this.translationService.get(label, order).subscribe((amt) => {
          optionsExc = amt;
          textString = optionsExc.replace('exchangeSymbol', order.exchgToSymId);
        });
      }
    } else if (fiveTwoNineOrder.transactionType === TransactionType.BUY) {
      textString = 'labels.orderSummary.twoPartMsgB_529';
    } else {
      textString = 'labels.orderSummary.twoPartMsg_529';
    }
    return textString;
  }

  getFinalTextFor529(textString: string, order: any, amount: any, optionsExc: any, dvre: string, label: string): string {
    const fiveTwoNineOrder = order as MutualfundTradingOrder;
    let options = ' ';
    let prospectusLabel = ' ';
    let isRedeemOrder = false;
    if (fiveTwoNineOrder.solicited) {
      options = 'labels.orderSummary.advisorTextSummary';
    }
    if (this.isRedeemOrRedeemAll(fiveTwoNineOrder.transactionType)) {
      isRedeemOrder = true;
      prospectusLabel = '';
    }
    if (fiveTwoNineOrder.readProspectus && !isRedeemOrder) {
      prospectusLabel = 'labels.orderSummary.prospectusText';
    }
    //
    if (!!fiveTwoNineOrder.transactionType) {
      this.transactionLabel = this.formatTransactionType(fiveTwoNineOrder);
      textString = textString.replace('{transactionType}', this.transactionLabel);
    }
    if (!!amount) {
      textString = textString.replace('{{amount}}', amount);
    }
    if (!!optionsExc) {
      this.translationService.get(optionsExc, order).subscribe((optionsExecMsg) => {
        optionsExc = optionsExecMsg;
        textString = textString.replace('{{optionsExc}}', optionsExc);
      });
    }
    if (!!dvre) {
      this.translationService.get(dvre, order).subscribe((dvreMsg) => {
        dvre = dvreMsg;
        textString = textString.replace('{{dvreType}}', dvre);
      });
    }
    if (!!prospectusLabel) {
      this.translationService.get(prospectusLabel, order).subscribe((prospMsg) => {
        prospectusLabel = prospMsg;
        textString = textString.replace('{{prospectusLabel}}', prospectusLabel);
      });
    } else {
      textString = textString.replace('{{prospectusLabel}}', '');
    }
    if (!!options) {
      this.translationService.get(options, order).subscribe((optionsMsg) => {
        options = optionsMsg;
        textString = textString.replace('{{options}}', options);
      });
    }
    if (!!label) {
      this.translationService.get(label, order).subscribe((labelMsg) => {
        label = labelMsg;
        textString = textString.replace('{{label}}', label);
      });
    }
    return textString;
  }

  generateOrderTypeMessage(order) {
    const equityOrder = order as TradingExchangeOrder;
    let textString = '';

    if (this.tradingService.currentContainer === 'Equity') {
      textString = this.getOrderTypeMessageEquity(equityOrder);
      order = this.processOrderEquity(order);
    } else if (this.tradingService.currentContainer === 'Option') {
      textString = this.getOrderTypeMessageOption(equityOrder);
      order = this.processOrderOption(order);
    }

    if (textString) {
      this.translationService.get(textString, order).subscribe((orderTypeTxt) => {
        textString = orderTypeTxt;
      });
    }
    return textString;
  }

  getOrderTypeMessageEquity(equityOrder: TradingExchangeOrder) {
    let textString = '';
    let orderType = equityOrder.orderType;
    let transactionType = equityOrder.transactionType;
    if ((orderType === OrderType.TRAILING_STOP_DOLLAR && equityOrder.trailDollarQty) ||
      (orderType === OrderType.TRAILING_STOP_PCT && equityOrder.trailPctQty)) {
      textString = this.getTextForTrailingStop(transactionType);

    } else if (equityOrder.limitOffset &&
      ((orderType === OrderType.TRAILING_STOP_LIMIT_DOLLAR || orderType === OrderType.TRAILING_STOP_LIMIT_AON) && equityOrder.trailDollarQty) ||
      (orderType === OrderType.TRAILING_STOP_LIMIT_PCT || orderType === OrderType.TRAILING_STOP_LIMIT_PCT_AON) && equityOrder.trailPctQty) {
      textString = this.getTextForTrailingStopLimit(transactionType);

    } else if (equityOrder.limitPrice && equityOrder.stopPrice) {
      textString = 'labels.orderSummary.stopLimitMsg';
    } else if (equityOrder.limitPrice) {
      textString = this.getTextForLimitPrice(orderType);

    } else if (equityOrder.stopPrice) {
      textString = 'labels.orderSummary.stopMsg';
    } else if (orderType === OrderType.MARKET) {
      textString = 'labels.orderSummary.marketMsg';
    }
    return textString;
  }

  getTextForTrailingStop(transactionType: TransactionType): string {
    let textString = '';
    if (transactionType === TransactionType.BUY || transactionType === TransactionType.BUY_TO_COVER) {
      textString = 'labels.orderSummary.trailingStopBuyMsg';
    } else if (transactionType === TransactionType.SELL_FROM_PORTFOLIO || transactionType === TransactionType.SELL_SHORT || transactionType === TransactionType.SELL_ALL) {
      textString = 'labels.orderSummary.trailingStopSellMsg';
    }
    return textString;
  }

  getTextForTrailingStopLimit(transactionType: TransactionType): string {
    let textString = '';
    if (transactionType === TransactionType.BUY || transactionType === TransactionType.BUY_TO_COVER) {
      textString = 'labels.orderSummary.trailingStopLimitBuyMsg';
    } else if (transactionType === TransactionType.SELL_FROM_PORTFOLIO || transactionType === TransactionType.SELL_SHORT || transactionType === TransactionType.SELL_ALL) {
      textString = 'labels.orderSummary.trailingStopLimitSellMsg';
    }
    return textString;
  }

  getTextForLimitPrice(orderType: OrderType): string {
    let textString = '';
    if (orderType === OrderType.AON) {
      textString = 'labels.orderSummary.limitMsgAON';
    } else if (orderType === OrderType.FOK) {
      textString = 'labels.orderSummary.limitMsgFOK';
    } else {
      textString = 'labels.orderSummary.limitMsg';
    }
    return textString;
  }

  processOrderEquity(order: any) {
    let orderType = order.orderType;
    let newLanguage = this.language === 'es' ? 'es' : '';
    if ((orderType === OrderType.TRAILING_STOP_DOLLAR) && order.trailDollarQty) {
      order.trailingAmt = this.currencyPipe.transform(order.trailDollarQty, '', 'symbol-narrow', '', newLanguage);

    } else if ((orderType === OrderType.TRAILING_STOP_PCT) && order.trailPctQty) {
      order.trailingAmt = Number(order.trailPctQty / 100).toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 0 });

    } else if ((orderType === OrderType.TRAILING_STOP_LIMIT_DOLLAR || orderType === OrderType.TRAILING_STOP_LIMIT_AON)
      && order.trailDollarQty && order.limitOffset) {
      order.trailingAmt = this.currencyPipe.transform(order.trailDollarQty, '', 'symbol-narrow', '', newLanguage);
      order.limitOffset = this.getLimitOffset(order.limitOffset, newLanguage);
    } else if ((orderType === OrderType.TRAILING_STOP_LIMIT_PCT || orderType === OrderType.TRAILING_STOP_LIMIT_PCT_AON) && order.trailPctQty && order.limitOffset) {
      order.trailingAmt = Number(order.trailPctQty / 100).toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 0 });
      order.limitOffset = this.getLimitOffset(order.limitOffset, newLanguage);

    } else if (order.limitPrice && order.stopPrice) {
      order.stopPrice = this.convertToDecimal(order.stopPrice);
      order.limitPrice = this.convertToDecimal(order.limitPrice);
    } else if (order.limitPrice) {
      order.limitPrice = this.convertToDecimal(order.limitPrice);

    } else if (order.stopPrice) {
      order.stopPrice = this.convertToDecimal(order.stopPrice);
    }
    return order;
  }

  getLimitOffset(limitOffset: any, newLanguage: string): any {
    return isNaN(limitOffset) ? 'null' : this.currencyPipe.transform(limitOffset, '', 'symbol-narrow', '', newLanguage);
  }

  getOrderTypeMessageOption(equityOrder: TradingExchangeOrder) {
    let textString = '';
    let orderType = equityOrder.orderType;

    if (equityOrder.limitPrice && equityOrder.stopPrice) {
      textString = 'labels.orderSummary.stopLimitMsg';
    } else if (equityOrder.limitPrice) {
      if (orderType === OrderType.AON) {
        textString = 'labels.orderSummary.limitMsgAON';
      } else if (orderType === OrderType.FOK) {
        textString = 'labels.orderSummary.limitMsgFOK';
      } else {
        textString = 'labels.orderSummary.limitMsg';
      }
    } else if (equityOrder.stopPrice) {
      textString = 'labels.orderSummary.stopMsg';
    } else if (orderType === OrderType.MARKET) {
      textString = 'labels.orderSummary.marketMsg';
    }
    return textString;
  }

  processOrderOption(order: any) {
    if (order.limitPrice && order.stopPrice) {
      order.stopPrice = this.convertToDecimal(order.stopPrice);
      order.limitPrice = this.convertToDecimal(order.limitPrice);
    } else if (order.limitPrice) {
      order.limitPrice = this.convertToDecimal(order.limitPrice);
    } else if (order.stopPrice) {
      order.stopPrice = this.convertToDecimal(order.stopPrice);
    }
    return order;
  }

  convertToDecimal(stopLimitPrice) {
    return '$' + this.decimalPipe.transform(stopLimitPrice, '1.2-4', this.language === 'es' ? 'es' : '');
  }

  generateTextTrailingStop(order, orderTypeMessage) {
    const equityOrder = order as TradingExchangeOrder;
    let orderType = equityOrder.orderType;
    let options = '';
    let isAON = false;
    if (orderType === OrderType.TRAILING_STOP_LIMIT_AON || orderType === OrderType.TRAILING_STOP_LIMIT_PCT_AON) {
      isAON = true;
      options = 'labels.orderSummary.TrailingStopAON';
    }

    let text = this.getTrailingStopText(orderType);
    let textString = this.getTrailingStopTextString(equityOrder, orderTypeMessage);

    if (textString) {
      this.translationService.get(textString, order).subscribe((trailingText) => {
        this.translationService.get(text, order).subscribe((transactionTxt) => {
          if (!!orderTypeMessage) {
            trailingText = trailingText.replace('{{orderTypeMessage}}', orderTypeMessage);
            trailingText = trailingText.replace('{{text}}', transactionTxt);
          }

          if (isAON) {
            this.translationService.get(options, order).subscribe((msgAON) => {
              trailingText = trailingText.replace('{{options}}.', msgAON);
            });
          } else {
            trailingText = trailingText.replace('{{options}}.', '');
          }
        });
        this.orderSummaryText = trailingText;
      });
    }
    return textString;
  }

  getTrailingStopText(orderType: OrderType): string {
    let text = '';
    if (orderType === OrderType.TRAILING_STOP_DOLLAR || orderType === OrderType.TRAILING_STOP_PCT) {
      if (this.isBuyOrder) {
        text = 'labels.orderSummary.trailingBuy';
      } else {
        text = 'labels.orderSummary.trailingSell';
      }
    }
    if (orderType === OrderType.TRAILING_STOP_LIMIT_DOLLAR || orderType === OrderType.TRAILING_STOP_LIMIT_PCT) {
      if (this.isBuyOrder) {
        text = 'labels.orderSummary.trailingStopLimitBuy';
      } else {
        text = 'labels.orderSummary.trailingStopLimitSell';
      }
    }
    if (orderType === OrderType.TRAILING_STOP_LIMIT_AON || orderType === OrderType.TRAILING_STOP_LIMIT_PCT_AON) {
      if (this.isBuyOrder) {
        text = 'labels.orderSummary.trailingStopLimitBuy';
      } else {
        text = 'labels.orderSummary.trailingStopLimitSell';
      }
    }
    return text;
  }

  getTrailingStopTextString(equityOrder: TradingExchangeOrder, orderTypeMessage: any): string {
    let textString = '';
    if (equityOrder.transactionType && equityOrder.quote.symId && equityOrder.quote.description && equityOrder.ordQty && equityOrder.orderType
      && equityOrder.duration && equityOrder.accountType && equityOrder.solicited) {
      textString = 'labels.orderSummary.sevenPartMsgTS';
    } else if (equityOrder.transactionType && equityOrder.quote.symId && equityOrder.quote.description && equityOrder.ordQty && equityOrder.orderType && orderTypeMessage
      && equityOrder.duration && equityOrder.accountType) {
      textString = 'labels.orderSummary.sixPartMsgTS';
    } else if (equityOrder.transactionType && equityOrder.quote && equityOrder.quote.symId && equityOrder.quote.description
      && equityOrder.ordQty && orderTypeMessage && equityOrder.duration) {
      textString = 'labels.orderSummary.fivePartMsgTS';
    } else if (equityOrder.transactionType && equityOrder.quote && equityOrder.quote.symId && equityOrder.quote.description && equityOrder.ordQty && equityOrder.orderType
      && orderTypeMessage) {
      textString = 'labels.orderSummary.fourPartMsgTS';
    } else if (equityOrder.transactionType && equityOrder.quote && equityOrder.quote.symId && equityOrder.quote.description && equityOrder.ordQty) {
      textString = 'labels.orderSummary.threePartMsg';
    } else if (equityOrder.transactionType && equityOrder.quote && equityOrder.quote.symId && equityOrder.quote.description) {
      textString = 'labels.orderSummary.twoPartMsg';
      this.toggleOn = true;
    }
    return textString;
  }

  formatOrderData(order) {
    order.accountType = this.lowerCasePipe.transform(order.accountType);
    let durationAvailable = false;
    order.commission.principalAmt = this.getprincipalAmt(order);


    if (this.tradingService.currentContainer === 'Option') {
      order = this.formatOrderDataOption(order);
      durationAvailable = order.duration ? true : false;

    } else if (this.tradingService.currentContainer === 'Equity') {
      order = this.formatOrderDataEquity(order);
      durationAvailable = order.duration ? true : false;

    } else if (this.tradingService.currentContainer === 'Extended') {
      order = this.formatOrderDataExtendedHrs(order);
      durationAvailable = order.duration ? true : false;

    } else if (this.tradingService.currentContainer === 'MutualFund' || this.tradingService.currentContainer === 'Plan529') {
      order = this.formatOrderDataMFOrPlan529(order);
      durationAvailable = order.duration ? true : false;
    }

    if (durationAvailable) {
      this.translationService.get(order.duration, order).subscribe((durationTxt) => {
        order.duration = this.lowerCasePipe.transform(durationTxt);
      });
    }
    return order;
  }

  getprincipalAmt(order): any {
    if (!!order.commission?.principalAmt && order.commission.principalAmt >= 0) {
      order.commission.principalAmt = this.checkMarketOrdEntitlementsforEstPrincipal(order);
    } else {
      this.notAvailableLabel = 'labels.quoteNotAvailable';
      this.translationService.get(this.notAvailableLabel, order).subscribe((quoteTxt) => {
        this.notAvailableLabel = quoteTxt;
      });
      order.commission.principalAmt = this.notAvailableLabel;
    }
    return order.commission.principalAmt;
  }

  formatOrderDataOption(order): any {
    if (order.quote && order.strikePrice && order.expirationDate) {
      order.strikePrice = this.currencyPipe.transform(order.strikePrice, '', 'symbol-narrow', '', this.language === 'es' ? 'es' : '');
      order.expirationDate = order.expirationDate.substring(0, 10);
      order.expirationDate = this.datePipe.transform(order.expirationDate, 'longDate');
      order.transactionType = this.formatTransactionType(order);
    }
    if (order.duration) {
      if (order.duration === OrderDuration.DAY) {
        order.duration = 'labels.orderSummary.durationMsgDay';
      } else if (order.duration === OrderDuration.GTC) {
        order.duration = 'labels.orderSummary.durationMsgGTC';
      }
    }
    return order;
  }

  formatOrderDataEquity(order): any {
    order.transactionType = this.formatTransactionType(order);
    if (order.duration) {
      if (order.duration === OrderDuration.DAY) {
        order.duration = 'DURATION.DAY';
      } else if (order.duration === OrderDuration.GTC) {
        order.duration = 'DURATION.GTC';
      }
    }
    return order;
  }

  formatOrderDataExtendedHrs(order): any {
    if (order.duration) {
      if (order.duration === OrderDuration.PREMKT) {
        order.duration = 'DURATION.PREMKT';
      } else if (order.duration === OrderDuration.AFTMKT) {
        order.duration = 'DURATION.AFTMKT';
      }
    }
    return order;
  }

  formatOrderDataMFOrPlan529(order): any {
    if (!!order.dollarQty || !!order.shareQty) {
      order.dollarQty = this.currencyPipe.transform(order.dollarQty, '', 'symbol-narrow', '', this.language === 'es' ? 'es' : '');
      order.shareQty = this.decimalPipe.transform(order.shareQty, '1.2-4', this.language === 'es' ? 'es' : '');
    }
    if (this.tradingService.currentContainer === 'Plan529') {
      this.transactionLabel = this.formatTransactionType(order);

    }
    return order;
  }

  checkMarketOrdEntitlementsforEstPrincipal(order) {
    if (isEstimatedValueApplicable(this.tradingService.tradingEntitlements, order)) {
      order.commission.principalAmt = this.currencyPipe.transform(order.commission.principalAmt, '', 'symbol-narrow', '', this.language === 'es' ? 'es' : '');
    } else {
      this.notAvailableLabel = 'labels.quoteNotAvailable';
      this.translationService.get(this.notAvailableLabel, order).subscribe((quoteTxt) => {
        this.notAvailableLabel = quoteTxt;
      });
      order.commission.principalAmt = this.notAvailableLabel;

    }

    return order.commission.principalAmt;
  }

  formatTransactionType(order) {
    let transactionLabel = '';
    if (order.transactionType) {
      if (this.tradingService.currentContainer === 'Equity' || this.tradingService.currentContainer === 'Extended') {
        transactionLabel = this.getTransactionLabelForEquityAndExtendedHrs(order);

      } else if (this.tradingService.currentContainer === 'Option') {
        transactionLabel = this.getTransactionLabelForOptions(order);

      } else if (this.tradingService.currentContainer === 'MutualFund' || this.tradingService.currentContainer === 'Plan529') {
        transactionLabel = this.getTransactionLabelForMutualFundAndPlan529(order);
      }
    }
    if (transactionLabel) {
      this.translationService.get(transactionLabel, order).subscribe((transactionText) => {
        transactionLabel = this.lowerCasePipe.transform(transactionText);
      });
    }

    return transactionLabel;
  }

  getTransactionLabelForEquityAndExtendedHrs(order: any): string {
    let transactionLabel = '';
    if (order.transactionType === TransactionType.BUY) {
      transactionLabel = 'TRANSACTION_TYPE.BUY';
    } else if (order.transactionType === TransactionType.BUY_TO_COVER) {
      transactionLabel = 'TRANSACTION_TYPE.BUY_TO_COVER';
    } else if (order.transactionType === TransactionType.SELL_ALL) {
      transactionLabel = 'TRANSACTION_TYPE.SELL_ALL';
    } else if (order.transactionType === TransactionType.SELL_FROM_PORTFOLIO) {
      transactionLabel = 'TRANSACTION_TYPE.SELL_FROM_PORTFOLIO';
    } else if (order.transactionType === TransactionType.SELL_SHORT) {
      transactionLabel = 'TRANSACTION_TYPE.SELL_SHORT';
    }
    return transactionLabel;
  }

  getTransactionLabelForOptions(order: any): string {
    let transactionLabel = '';
    if (order.transactionType === TransactionType.BUY_TO_OPEN) {
      transactionLabel = this.getTransactionLabelForBuyToOpen(order.chainType);
    } else if (order.transactionType === TransactionType.SELL_TO_OPEN) {
      transactionLabel = this.getTransactionLabelForSellToOpen(order.chainType);
    } else if (order.transactionType === TransactionType.CLOSE_CALLS_OR_PUTS) {
      transactionLabel = this.getTransactionLabelForCloseCallsOrPuts(order);
    } else if (order.transactionType === TransactionType.SELL_COVERED_CALLS) {
      transactionLabel = 'SELL_COVERED_CALLS';
    }
    return transactionLabel;
  }

  getTransactionLabelForBuyToOpen(optionType: any): string {
    let transactionLabel = '';
    if (optionType === 'C') {
      transactionLabel = 'TRANSACTION_TYPE.BUY_CALL_TO_OPEN';
    } else if (optionType === 'P') {
      transactionLabel = 'TRANSACTION_TYPE.BUY_PUT_TO_OPEN';
    }
    return transactionLabel;
  }

  getTransactionLabelForSellToOpen(optionType: any): string {
    let transactionLabel = '';
    if (optionType === 'C') {
      transactionLabel = 'TRANSACTION_TYPE.SELL_CALL_TO_OPEN';
    } else if (optionType === 'P') {
      transactionLabel = 'TRANSACTION_TYPE.SELL_PUT_TO_OPEN';
    }
    return transactionLabel;
  }

  getTransactionLabelForCloseCallsOrPuts(order: any): string {
    let transactionLabel = '';
    if (order.chainType === 'C') {
      if (order.sellToOpenPositionSelected) {
        transactionLabel = 'TRANSACTION_TYPE.BUY_CALL_TO_CLOSE';
      } else {
        transactionLabel = 'TRANSACTION_TYPE.CLOSE_EXISTING_CALL';
      }
    } else if (order.chainType === 'P') {
      if (order.sellToOpenPositionSelected) {
        transactionLabel = 'TRANSACTION_TYPE.BUY_PUT_TO_CLOSE';
      } else {
        transactionLabel = 'TRANSACTION_TYPE.CLOSE_EXISTING_PUT';
      }
    }
    return transactionLabel;
  }

  getTransactionLabelForMutualFundAndPlan529(order: any): string {
    let transactionLabel = '';
    if (order.transactionType === TransactionType.BUY) {
      transactionLabel = 'TRANSACTION_TYPE.BUY';
    } else if (order.transactionType === TransactionType.BUY_MONEY_MARKET_SWEEP) {
      transactionLabel = 'TRANSACTION_TYPE.BUY_MONEY_MARKET_SWEEP';
    } else if (order.transactionType === TransactionType.REDEEM) {
      transactionLabel = 'TRANSACTION_TYPE.REDEEM';
    } else if (order.transactionType === TransactionType.REDEEM_ALL) {
      transactionLabel = 'TRANSACTION_TYPE.REDEEM_ALL';
    } else if (order.transactionType === TransactionType.EXCHANGE) {
      transactionLabel = 'TRANSACTION_TYPE.EXCHANGE';
    } else if (order.transactionType === TransactionType.EXCHANGE_ALL) {
      transactionLabel = 'TRANSACTION_TYPE.EXCHANGE_ALL';
    } else if (order.transactionType === TransactionType.SELL_AND_BUY) {
      transactionLabel = 'TRANSACTION_TYPE.SELL_AND_BUY';
    } else if (order.transactionType === TransactionType.SELL_ALL_AND_BUY) {
      transactionLabel = 'TRANSACTION_TYPE.SELL_ALL_AND_BUY';
    }
    return transactionLabel;
  }

  ngOnDestroy() {
    this.onRawOrderSubject?.unsubscribe();
    this.holdingsSub?.unsubscribe();
    this.estPrincipalSub?.unsubscribe();
  }
}
